/**
 * OjetaList — Phase 7.1 list workspace for ojetá vozidla.
 *
 * Shape extrapolated from `vozidla/VozidlaList.tsx` — same Crown / Toolband /
 * DataTable / Inspector composition, just pointed at `/v1/ojeta-vozidla` and
 * carrying the used-vehicle subtab set (vozidla / nabídky / smlouvy / výkup).
 *
 * URL search params (`?stav=&brand=&model=&search=`) drive filters; the search
 * input feeds the API's free-text `search` filter (ORed across SPZ, VIN,
 * provedení, model name, and the customer's name). The model filter is brand-scoped — picking a
 * brand enables it.
 */

import { useOjetaSpravaTab } from './tabs';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Badge } from '@/components/Badge';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { ColorSwatch, vehicleColorToHex } from '@/components/ColorSwatch';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn, type RowTint } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Inspector, FieldRow } from '@/components/Inspector';
import { Kbd } from '@/components/Kbd';
import { LoadMoreBar } from '@/components/LoadMoreBar';
import { Menu } from '@/components/Menu';
import { Pill } from '@/components/Pill';
import { SearchInput } from '@/components/SearchInput';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { otherFirmaName, useActiveFirmaId } from '@/modules/skupiny/otherFirma';
import { RozsahChip } from '@/modules/skupiny/RozsahChip';
import { useRozsah } from '@/modules/skupiny/rozsah';
import { formatDate, formatInt, formatKm } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import {
  useOjetaBrandsQuery,
  useOjetaListInfiniteQuery,
  useOjetaModelsQuery,
  type OjetoVozidlo,
} from './api';
import {
  DEFAULT_STAV,
  STAV_LABEL,
  type StavFilter,
  isRegistrovany,
  ojetaFiltersFromSearch,
  stavBadgeLabel,
  stavBadgeTone,
  deadlineDays,
  deadlineHint,
  deadlineTone,
  TERMIN_SHORT_LABEL,
  type TerminDruh,
  vehicleDisplayName,
} from './helpers';

type Subtab = 'vozidla' | 'upravena' | 'nabidky' | 'smlouvy' | 'vykup' | 'sprava';

// Model (capped) + provedení share the slack — see VozidlaList.
// Provedení and Termíny share the slack (1.2fr : 0.8fr) so a wide screen widens
// both instead of handing everything to provedení; Termíny's floor fits its
// longest line ("Servis · po termínu 1079 dní") without wrapping.
const COLUMNS_TEMPLATE = '40px 90px minmax(110px,180px) minmax(150px,1.2fr) 110px 100px minmax(210px,0.8fr) minmax(110px,160px)';

export function OjetaList() {
  const navigate = useNavigate();
  const sprava = useOjetaSpravaTab();
  const { isPhone } = useBreakpoint();
  const { can } = useModuleAccess();
  const canWrite = can('ojeta_vozidla', 'write');
  const urlSearch = useSearch({ from: '/ojeta-vozidla' });
  const stav: StavFilter = urlSearch.stav ?? DEFAULT_STAV;
  const brandId: number | null = urlSearch.brand ?? null;
  const modelId: number | null = urlSearch.model ?? null;
  const [subtab, setSubtab] = useState<Subtab>('vozidla');
  const skupina = useRozsah('ojeta_vozidla');
  const activeFirmaId = useActiveFirmaId();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [searchInput, setSearchInput] = useState<string>(urlSearch.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  useEffect(() => {
    const fromUrl = urlSearch.search ?? '';
    if (fromUrl !== searchInput && fromUrl !== debouncedSearch) {
      setSearchInput(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch.search]);

  useEffect(() => {
    const desired = debouncedSearch || undefined;
    if ((urlSearch.search ?? undefined) === desired) return;
    navigate({
      to: '/ojeta-vozidla',
      search: { ...urlSearch, search: desired },
      replace: true,
    });
  }, [debouncedSearch, urlSearch, navigate]);

  function setStav(next: StavFilter) {
    navigate({
      to: '/ojeta-vozidla',
      search: { ...urlSearch, stav: next === DEFAULT_STAV ? undefined : next },
      replace: true,
    });
  }
  function setBrandId(next: number | null) {
    navigate({
      to: '/ojeta-vozidla',
      // Brand change resets the model — model is brand-scoped, so a stale
      // value here would filter the list against a model from a different brand.
      search: { ...urlSearch, brand: next ?? undefined, model: undefined },
      replace: true,
    });
  }
  function setModelId(next: number | null) {
    navigate({
      to: '/ojeta-vozidla',
      search: { ...urlSearch, model: next ?? undefined },
      replace: true,
    });
  }

  const onSubtabChange = (next: Subtab) => {
    if (next === 'sprava') {
      navigate({ to: '/ojeta-vozidla/sprava' });
      return;
    }
    if (next === 'upravena') {
      navigate({ to: '/ojeta-vozidla/upravena' });
      return;
    }
    if (next === 'nabidky') {
      navigate({ to: '/ojeta-vozidla/nabidky' });
      return;
    }
    if (next === 'smlouvy') {
      navigate({ to: '/ojeta-vozidla/smlouvy' });
      return;
    }
    if (next === 'vykup') {
      navigate({ to: '/ojeta-vozidla/vykup' });
      return;
    }
    setSubtab(next);
  };

  const filters = useMemo(
    () =>
      ojetaFiltersFromSearch({
        stav,
        brand: brandId ?? undefined,
        model: modelId ?? undefined,
        search: debouncedSearch,
      }),
    [brandId, modelId, stav, debouncedSearch],
  );
  const listFilters = useMemo(
    () => (skupina.rozsah === 'skupina' ? { ...filters, rozsah: 'skupina' as const } : filters),
    [filters, skupina.rozsah],
  );

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOjetaListInfiniteQuery(listFilters);
  const brands = useOjetaBrandsQuery();
  const models = useOjetaModelsQuery(brandId);

  const vehicles = useMemo(
    () => data?.pages.flatMap((p) => p.vozidla) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;
  const selected = vehicles.find((v) => v.id_vozidla === selectedId) ?? vehicles[0] ?? null;
  const brandName = useMemo(() => {
    if (brandId == null) return null;
    return brands.data?.brands.find((b) => b.id === brandId)?.nazev ?? null;
  }, [brandId, brands.data]);
  const modelName = useMemo(() => {
    if (modelId == null) return null;
    return models.data?.models.find((m) => m.id === modelId)?.nazev_modelu ?? null;
  }, [modelId, models.data]);

  // The server counts the whole result; the loaded rows are only the pages fetched so far.
  const pocty = data?.pages[0]?.pocty;
  const counts = {
    total,
    skladovy: pocty?.skladovy ?? 0,
    zakaznicky: pocty?.zakaznicky ?? 0,
    predane: pocty?.predane ?? 0,
  };

  return (
    <Shell
      rail={<AppRail active="ojeta" />}
      inspector={<OjetoInspector vehicle={selected} activeFirmaId={activeFirmaId} />}
    >
      <Crown>
        <Breadcrumb segments={[{ label: 'Ojetá vozidla' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>Ojetá vozidla</DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Celkem', value: formatInt(counts.total) },
              { label: 'Skladová', value: formatInt(counts.skladovy) },
              { label: 'Zákaznická', value: formatInt(counts.zakaznicky) },
              { label: 'Předaná', value: formatInt(counts.predane) },
            ]}
          />
        </Crown.Row>
        <Subtabs
          aria-label="Ojetá vozidla — sekce"
          value={subtab}
          onChange={onSubtabChange}
          items={[
            { value: 'vozidla', label: 'Vozidla', count: counts.total },
            { value: 'upravena', label: 'Upravená vozidla' },
            { value: 'nabidky', label: 'Nabídky' },
            { value: 'smlouvy', label: 'Smlouvy' },
            { value: 'vykup', label: 'Výkup' },
            ...sprava,
          ]}
          trailing={
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>→</span>
              <Kbd>⌘K</Kbd>
            </span>
          }
        />
      </Crown>

      <Toolband>
        <SearchInput
          placeholder="SPZ, VIN, provedení, model, zákazník…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          wrapperClassName="max-w-[360px] flex-1"
        />
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={stav !== 'all'}>
              {STAV_LABEL[stav]}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr stavu</Menu.Label>
            {(Object.keys(STAV_LABEL) as StavFilter[]).map((key) => (
              <Menu.CheckItem
                key={key}
                checked={stav === key}
                onCheckedChange={(checked) => {
                  if (checked) setStav(key);
                }}
              >
                {STAV_LABEL[key]}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <RozsahChip skupina={skupina} />
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={brandId != null}>
              {brandName ?? 'Značka'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr značky</Menu.Label>
            <Menu.Item onSelect={() => setBrandId(null)}>Všechny značky</Menu.Item>
            <Menu.Separator />
            {(brands.data?.brands ?? []).map((b) => (
              <Menu.CheckItem
                key={b.id}
                checked={brandId === b.id}
                onCheckedChange={(checked) => setBrandId(checked ? b.id : null)}
              >
                {b.nazev}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Menu>
          <Menu.Trigger asChild>
            <Chip
              icon={<Icon name="filter" size={12} />}
              active={modelId != null}
              disabled={brandId == null}
            >
              {modelName ?? 'Model'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr modelu</Menu.Label>
            <Menu.Item onSelect={() => setModelId(null)}>Všechny modely</Menu.Item>
            <Menu.Separator />
            {(models.data?.models ?? []).map((m) => (
              <Menu.CheckItem
                key={m.id}
                checked={modelId === m.id}
                onCheckedChange={(checked) => setModelId(checked ? m.id : null)}
              >
                {m.nazev_modelu}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Toolband.Spacer />
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" size={12} />}
            onClick={() => navigate({ to: '/ojeta-vozidla/novy' })}
          >
            Nové vozidlo
          </Button>
        )}
      </Toolband>

      <div
        data-testid="ojeta-page"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1"
      >
        <DataTable<OjetoVozidlo>
          aria-label="Seznam ojetých vozidel"
          variant="bordered"
          columns={ojetaColumns(stav, activeFirmaId)}
          data={isLoading ? [] : vehicles}
          getRowId={(v) => String(v.id_vozidla)}
          selectedId={selected ? String(selected.id_vozidla) : undefined}
          onRowClick={(v) =>
            // Phone (14.3): no inspector exists — tapping a card navigates to
            // the detail route, forwarding the list cohort for its BackBar.
            isPhone
              ? navigate({
                  to: '/ojeta-vozidla/$id',
                  params: { id: String(v.id_vozidla) },
                  search: urlSearch,
                })
              : setSelectedId(v.id_vozidla)
          }
          getRowTint={(v) =>
            v.rezervace ? ('amber' satisfies RowTint) : undefined
          }
          empty={
            isLoading ? (
              <ListSkeleton />
            ) : isError ? (
              <EmptyState
                eyebrow="Chyba"
                title="Vozidla se nepodařilo načíst"
                description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
              />
            ) : (
              <EmptyState
                eyebrow="Prázdné"
                title="Žádná ojetá vozidla"
                description="Zkuste rozšířit filtry nebo vytvořte nové vozidlo."
              />
            )
          }
          className="min-h-0 flex-1"
        />
      </div>
      <LoadMoreBar
        loaded={vehicles.length}
        total={total}
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        unit="vozidel"
      />
    </Shell>
  );
}

function ojetaColumns(stav: StavFilter, activeFirmaId: number | null): DataTableColumn<OjetoVozidlo>[] {
  const widths = COLUMNS_TEMPLATE.split(' ');
  // Customer-owned cars show the customer instead of the price (as the new-car list does); a car handed over to a
  // customer keeps its handover date as a second line.
  const showCustomer = stav === 'zakaznicky' || stav === 'predany_zakaznikovi';
  return [
    {
      id: 'flag',
      header: '',
      width: widths[0],
      align: 'center',
      cell: (v) => <ReservationFlag rezervace={v.rezervace} />,
    },
    {
      id: 'spz',
      header: 'SPZ',
      width: widths[1],
      cell: (v) => (
        <span
          className={
            'font-mono text-[11.5px] tracking-[-0.02em] ' +
            (v.spz_vozu ? 'font-semibold text-ink' : 'text-ink-3')
          }
        >
          {v.spz_vozu || '—'}
        </span>
      ),
    },
    {
      id: 'model',
      header: 'Model',
      width: widths[2],
      cell: (v) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold tracking-[-0.01em] text-ink">
            {v.nazev_modelu ?? '—'}
          </span>
          <span className="truncate text-[11px] text-ink-3">{v.nazev_znacky ?? ''}</span>
        </div>
      ),
    },
    {
      id: 'prov',
      header: 'Provedení / Barva / Motor',
      width: widths[3],
      cell: (v) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[11.5px] text-ink-2">{v.provedeni || '—'}</span>
          <span className="flex items-center gap-1.5 truncate text-[10.5px] text-ink-3">
            {v.barva && <ColorSwatch color={vehicleColorToHex(v.barva)} size={8} />}
            <span className="truncate">{[v.barva, v.motor].filter(Boolean).join(' · ') || '—'}</span>
          </span>
        </div>
      ),
    },
    {
      id: 'vin',
      header: 'VIN',
      width: widths[4],
      cell: (v) => (
        <span className="truncate font-mono text-[10.5px] text-ink-3">
          {v.vin || '—'}
        </span>
      ),
    },
    showCustomer
      ? {
          id: 'zakaznik',
          header: 'Zákazník',
          width: 'minmax(130px,190px)',
          cell: (v) => {
            const name = v.jmeno_zakaznika?.trim();
            if (!name) return <span className="text-ink-3">—</span>;
            const id = Number(v.id_zakaznika);
            // A car of another firma of the group: its customer lives in that firma, so no link from here.
            const linkable = Number.isFinite(id) && id > 0 && !otherFirmaName(v, activeFirmaId);
            return (
              <span className="flex min-w-0 flex-col">
                {linkable ? (
                  <Link
                    to="/zakaznici/$id"
                    params={{ id: String(id) }}
                    className="truncate text-[12px] font-medium text-ink underline-offset-2 hover:underline"
                  >
                    {name}
                  </Link>
                ) : (
                  <span className="truncate text-[12px] text-ink-2">{name}</span>
                )}
                {stav === 'predany_zakaznikovi' && v.datum_predani && (
                  <span className="truncate font-mono text-[10.5px] text-ink-3">
                    Předáno {formatDate(v.datum_predani)}
                  </span>
                )}
              </span>
            );
          },
        }
      : {
          id: 'cena',
          header: stav === 'predany_dealerovi' ? 'Předáno' : 'Cena',
          width: widths[5],
          align: 'right',
          cell: (v) => {
            if (stav === 'predany_dealerovi') {
              return (
                <span className="font-mono text-[11px] text-ink-2">
                  {formatDate(v.datum_predani) || '—'}
                </span>
              );
            }
            const price = Number(v.prodejni_cena || 0);
            const has = price > 0;
            return (
              <span
                className={
                  'font-mono text-[11.5px] tracking-[-0.02em] ' +
                  (has ? 'font-semibold text-ink' : 'font-normal text-ink-3')
                }
              >
                {has ? `${formatInt(price)} Kč` : '—'}
              </span>
            );
          },
        },
    {
      id: 'terminy',
      header: 'Termíny',
      width: widths[6],
      cell: (v) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <DeadlineLine druh="stk" date={v.stk_platnost} />
          <DeadlineLine druh="servis" date={v.serviska} />
          {!v.stk_platnost && !v.serviska && (
            <span className="font-mono text-[10.5px] text-ink-3">—</span>
          )}
        </span>
      ),
    },
    {
      id: 'stav',
      header: 'Stav',
      width: widths[7],
      cell: (v) => (
        <span
          className="flex min-w-0 flex-col items-start gap-0.5"
          title={v.preveden_do_firma ? `Převedeno do firmy ${v.preveden_do_firma}` : undefined}
        >
          <Badge tone={stavBadgeTone(v)}>{stavBadgeLabel(v.stav_vozidla)}</Badge>
          {otherFirmaName(v, activeFirmaId) && v.firma_nazev && (
            <Badge tone="accent" size="sm" className="max-w-full">
              <span className="truncate" title={`Vozidlo firmy ${v.firma_nazev}`}>
                {v.firma_nazev}
              </span>
            </Badge>
          )}
        </span>
      ),
    },
  ];
}

function ReservationFlag({ rezervace }: { rezervace: string }) {
  const free = !rezervace;
  return (
    <span
      aria-hidden
      title={free ? 'K dispozici' : `Rezervace: ${rezervace}`}
      className={
        'relative grid size-3.5 place-items-center rounded-full border-[1.5px] ' +
        (free ? 'border-ok bg-ok text-paper' : 'border-warn text-warn')
      }
    >
      <span className="text-[9px] font-bold leading-none">{free ? '✓' : '!'}</span>
    </span>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-px p-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function OjetoInspector({ vehicle, activeFirmaId }: { vehicle: OjetoVozidlo | null; activeFirmaId: number | null }) {
  const urlSearch = useSearch({ from: '/ojeta-vozidla' });
  const navigate = useNavigate();
  if (!vehicle) {
    return (
      <Inspector>
        <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Vyberte vozidlo
        </div>
      </Inspector>
    );
  }

  const price = Number(vehicle.prodejni_cena || 0);
  const reg = isRegistrovany(vehicle);
  // A car of another firma of the group (combined list): its detail opens read-only, in its own firma.
  const otherFirma = otherFirmaName(vehicle, activeFirmaId);

  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — № {vehicle.id_vozidla} · {reg ? 'Registrované' : vehicle.stav_vozidla}
          </>
        }
        title={
          <>
            {vehicleDisplayName(vehicle) || vehicle.nazev_modelu || 'Vozidlo'}
            {vehicle.provedeni && (
              <span className="font-medium italic text-ink-2"> {vehicle.provedeni}.</span>
            )}
          </>
        }
        sub={
          <span className="inline-flex items-center gap-2.5">
            <Pill>{vehicle.spz_vozu || 'BEZ SPZ'}</Pill>
            {vehicle.barva && (
              <span className="inline-flex items-center gap-1.5">
                <ColorSwatch color={vehicleColorToHex(vehicle.barva)} size={10} />
                <span className="font-sans text-[12px] text-ink-2">{vehicle.barva}</span>
              </span>
            )}
          </span>
        }
      />
      <Inspector.Body>
        {otherFirma && (
          <Inspector.Section heading="— Firma">
            <FieldRow k="Vozidlo firmy" v={otherFirma} />
          </Inspector.Section>
        )}
        <Inspector.Section heading="— Cena">
          {price > 0 ? (
            <div className="flex items-baseline gap-1.5 font-sans text-[28px] font-bold tracking-[-0.02em] text-ink">
              {formatInt(price)}
              <small className="text-[14px] font-medium text-ink-3">Kč</small>
            </div>
          ) : (
            <div className="font-sans text-[20px] font-medium text-ink-3">—</div>
          )}
        </Inspector.Section>
        <Inspector.Section heading="— Specifikace">
          <FieldRow k="Značka" v={vehicle.nazev_znacky ?? '—'} />
          <FieldRow k="Motor" v={vehicle.motor || '—'} />
          <FieldRow k="Převodovka" v={vehicle.prevodovka || '—'} />
          <FieldRow k="Stav km" v={formatKm(vehicle.stav_km) ?? '—'} />
          <FieldRow
            k="1. registrace"
            v={vehicle.prvni_registrace ? formatDate(vehicle.prvni_registrace) : '—'}
          />
        </Inspector.Section>
        <Inspector.Section heading="— Termíny">
          <DeadlineRow k="Platnost STK" date={vehicle.stk_platnost} />
          <DeadlineRow k="Servisní prohlídka" date={vehicle.serviska} />
        </Inspector.Section>
        {vehicle.jmeno_zakaznika && (
          <Inspector.Section heading="— Zákazník">
            <FieldRow k="Jméno" v={vehicle.jmeno_zakaznika} />
            {vehicle.datum_predani && (
              <FieldRow k="Předáno" v={formatDate(vehicle.datum_predani)} />
            )}
          </Inspector.Section>
        )}
        {vehicle.vin && (
          <Inspector.Section heading="— VIN">
            <code className="block select-all break-all border border-line bg-bg p-2 font-mono text-[11px] tracking-[-0.02em] text-ink-2">
              {vehicle.vin}
            </code>
          </Inspector.Section>
        )}
        {vehicle.rezervace && (
          <Inspector.Section heading="— Rezervace">
            <p className="text-[12px] text-warn">{vehicle.rezervace}</p>
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/ojeta-vozidla/$id',
              params: { id: String(vehicle.id_vozidla) },
              search: urlSearch,
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}

/** Inspector row for a DATE deadline — date plus a warn/danger hint inside the
 *  30-day window; a muted dash when unset. */
function DeadlineRow({ k, date }: { k: string; date: string | null | undefined }) {
  const days = deadlineDays(date);
  const tone = deadlineTone(days);
  return (
    <FieldRow
      k={k}
      v={
        date && days != null ? (
          <span className="inline-flex flex-wrap items-baseline gap-1.5">
            {formatDate(date)}
            {tone && <Badge tone={tone}>{deadlineHint(days)}</Badge>}
          </span>
        ) : (
          '—'
        )
      }
    />
  );
}

/** One deadline line in the Termíny column: "STK · za 12 dní" in warn/danger
 *  colour inside the 30-day window, otherwise the plain date in muted ink.
 *  Renders nothing when the date is unset so the cell only lists what exists. */
function DeadlineLine({ druh, date }: { druh: TerminDruh; date: string | null | undefined }) {
  const days = deadlineDays(date);
  if (!date || days == null) return null;
  const tone = deadlineTone(days);
  const cls = tone === 'danger' ? 'text-red-text' : tone === 'warn' ? 'text-warn' : 'text-ink-3';
  return (
    <span className={`whitespace-nowrap font-mono text-[10.5px] tracking-[-0.01em] ${cls}`}>
      {TERMIN_SHORT_LABEL[druh]} · {tone ? deadlineHint(days) : formatDate(date)}
    </span>
  );
}
